import { useConfirmationModal } from '@bridebook/aura';
import { Copy, Mail } from '@bridebook/aura/src/icons';
import { useMessageGuestsModal } from '@bridebook/web/components/inbox/guests/guests-messages-container';
import { getScreenType } from '@bridebook/web/lib/app/selectors';
import { toggleSnackbar } from '@bridebook/web/lib/bbcommon/actions';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import { useDispatch, useSelector } from '@bridebook/web/lib/utils';
import { useCopyToClipboardWithFeedback } from '@bridebook/web/lib/utils/hooks/use-copy-to-clipboard-with-feedback';
import useTimeout from '@bridebook/web/lib/utils/hooks/use-timeout';
import { useShareWeddingWebsiteClick } from '@bridebook/web/lib/wedding-website/analytics/use-share-wedding-website-click';
import { useShareWeddingWebsite } from '@bridebook/web/lib/wedding-website/hooks/use-share-wedding-website';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useCallback } from 'react';

export const useGuestCommsRedirectShareModal = (linkToRsvp?: boolean) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { copy } = useCopyToClipboardWithFeedback();
  const isInWebsitePreviewContext = router.asPath.includes(UrlHelper.website.preview());
  const { openModal: openInterceptModal, closeModal } = useConfirmationModal();
  const { shareWeddingWebsite, websiteUrl } = useShareWeddingWebsite({ linkToRsvp });
  const { openModal: openMessageModal, isDisabled: isMessageGuestsDisabled } =
    useMessageGuestsModal();
  const { t } = useTranslation('inbox');
  const { schedule } = useTimeout();
  const { isDesktop } = useSelector(getScreenType);
  const { trackShareWeddingWebsiteClick } = useShareWeddingWebsiteClick();

  const handleCopyToClipboardDesktop = useCallback(() => {
    copy(websiteUrl);
    schedule(() => {
      dispatch(toggleSnackbar('success', t('common:share.linkCopiedToClipboard')));
      closeModal();
    }, 300);
  }, [closeModal, copy, dispatch, schedule, t, websiteUrl]);

  const handleCopyMobile = useCallback(() => {
    closeModal();
    shareWeddingWebsite();
    trackShareWeddingWebsiteClick({
      cta: 'Copy link',
      clickedLocation: isInWebsitePreviewContext
        ? 'weddingWebsites_preview'
        : 'weddingWebsitesPage',
    });
  }, [closeModal, isInWebsitePreviewContext, shareWeddingWebsite, trackShareWeddingWebsiteClick]);

  return useCallback(() => {
    openInterceptModal({
      title: t('guestComms.redirectShareModal.title'),
      text: t('guestComms.redirectShareModal.text'),
      // Message guests
      primaryButtonText: t('guestComms.redirectShareModal.primaryButtonText'),
      // Copy link
      secondaryButtonText: t('guestComms.redirectShareModal.secondaryButtonText'),
      onPrimaryButtonClick: () => {
        trackShareWeddingWebsiteClick({
          clickedLocation: isInWebsitePreviewContext
            ? 'weddingWebsites_preview'
            : 'weddingWebsitesPage',
          cta: 'Message guests',
          category: 'weddingWebsites',
        });
        openMessageModal();
      },
      primaryButtonProps: {
        icon: Mail,
        disabled: isMessageGuestsDisabled,
      },
      secondaryButtonProps: {
        icon: Copy,
        onClick: () => {
          if (isDesktop) {
            handleCopyToClipboardDesktop();
          } else {
            handleCopyMobile();
          }
        },
      },
    });
  }, [
    openInterceptModal,
    t,
    trackShareWeddingWebsiteClick,
    isInWebsitePreviewContext,
    openMessageModal,
    isDesktop,
    handleCopyToClipboardDesktop,
    handleCopyMobile,
    isMessageGuestsDisabled,
  ]);
};
